import type {
  GroupId,
  PreparedSearchEntry,
  SearchResultsByGroupId,
} from '../shared-types'

import fuzzysort from 'fuzzysort'
import { defineStore } from 'pinia'

import {
  focusedTypes,
  fuzzySearchThreshold,
} from '@app/application/account-mapping/constants'
import { prepareSearchItem } from '@app/application/account-mapping/store/utils/search'
import { useCommon } from './common'
import { useGroupList } from './group-list'
import { useLocale } from './locale'
import { walkParents } from './utils/hierarchy'
import type { SearchState } from './types'
import intersection from 'lodash/intersection'

export const useSearch = defineStore('accountMappingSearch', {
  state: (): SearchState => ({
    remapDropdownAIFilteredGroupIds: [],
    remapDropdownExternalFilteredGroupIds: [],
    remapDropdownShowGroup: 'ai',
  }),
  actions: {
    setRemapDropdownAIFitleredGroupIds(ids: Array<GroupId>) {
      this.remapDropdownAIFilteredGroupIds = [...new Set(ids)]
    },
    setRemapDropdownExternalFitleredGroupIds(ids: Array<string>) {
      this.remapDropdownExternalFilteredGroupIds = [...new Set(ids)]
    },
    setRemapDropdownShowGroup(value: 'ai' | 'external' | 'all') {
      this.remapDropdownShowGroup = value
    },
  },
  getters: {
    getPreparedSearchCollection(): Array<PreparedSearchEntry> {
      const commonStore = useCommon()
      const { supportedLocales } = useLocale()

      const accounts = commonStore.$state.accounts
      const groupList = commonStore.getGroupListPerDisplay
      const groupMappedAccounts = commonStore.$state.groupMappedAccounts

      let order = 0

      const result = groupList.reduce((acc, groupId) => {
        const group = commonStore.$state.groups[groupId]
        const preparedGroup = prepareSearchItem({
          itemId: groupId,
          item: group,
          type: focusedTypes.GROUP,
          order: order++,
          supportedLocales,
        })

        const preparedGroupAccounts = Object.keys(
          groupMappedAccounts[groupId] || {}
        ).reduce((acc, accountId) => {
          const preparedAccount = prepareSearchItem({
            itemId: accountId,
            item: accounts[accountId],
            type: focusedTypes.ACCOUNT,
            order: order++,
            supportedLocales,
          })

          return preparedAccount ? [...acc, preparedAccount] : acc
        }, [] as PreparedSearchEntry[])

        return preparedGroup
          ? [...acc, preparedGroup, ...preparedGroupAccounts]
          : [...acc, ...preparedGroupAccounts]
      }, [] as PreparedSearchEntry[])

      return result
    },
    getSearchResults(): Fuzzysort.KeysResults<PreparedSearchEntry> {
      const commonStore = useCommon()

      const searchTerm = commonStore.$state.searchTerm

      /*if (searchTerm.length === 0) {
        return []
      }*/

      const searchCollection = this.getPreparedSearchCollection

      return fuzzysort.go(searchTerm, searchCollection, {
        keys: [
          'preparedName',
          'preparedAccountRange',
          'preparedOriginalName',
          'preparedNumber',
        ],
        threshold: fuzzySearchThreshold,
      })
    },
    getSearchResultsByGroupId(): SearchResultsByGroupId {
      const commonStore = useCommon()

      const searchResults = this.getSearchResults

      if (commonStore.$state.searchTerm.length === 0) {
        return {}
      }

      const result = searchResults.reduce((acc, result) => {
        // Make the result objects reactive so that highlights get updated with search term updates

        acc[result.obj.itemId] = {
          type: result.obj.type,
          order: result.obj.order,
          highlights: {
            preparedName: result[0] ? { ...result[0] } : null,
            preparedAccountRange: result[1] ? { ...result[1] } : null,
            preparedOriginalName: result[2] ? { ...result[2] } : null,
            preparedNumber: result[3] ? { ...result[3] } : null,
          },
        }

        return acc
      }, {} as SearchResultsByGroupId)

      return result
    },
    getSearchResultsOrdered(): Array<{
      type: 'group' | 'account'
      id: string
    }> {
      const searchResultsByGroupId = this.getSearchResultsByGroupId

      if (Object.keys(searchResultsByGroupId).length === 0) {
        return []
      }

      return Object.keys(searchResultsByGroupId)
        .map((key) => ({
          id: key,
          type: searchResultsByGroupId[key].type,
          order: searchResultsByGroupId[key].order,
        }))
        .sort((a, b) => {
          if (a.order < b.order) {
            return -1
          }
          if (a.order > b.order) {
            return 1
          }
          return 0
        })
    },
    getRemapDropdownGroupIds(): Array<GroupId> {
      const commonStore = useCommon()
      const groupListStore = useGroupList()
      if (this.remapDropdownShowGroup === 'ai')
        return this.remapDropdownAIFilteredGroupIds

      if (this.remapDropdownShowGroup === 'external')
        return groupListStore.getExternallyMappedGroupIds

      return commonStore.getGroupListPerDisplay
    },
    getRemapDropdownGroupList(): Array<GroupId> {
      const commonStore = useCommon()
      const searchTerm = commonStore.$state.searchTerm
      const groupIdsPool = this.getRemapDropdownGroupIds

      // intersect with search results if there's a search term, otherwise return the entire pool
      const groupIds = searchTerm
        ? intersection(
            this.getSearchResultsOrdered.map((sr) => sr.id),
            groupIdsPool
          )
        : groupIdsPool

      const groupIdsAndAncestorIds = groupIds.reduce((acc, groupId) => {
        const ancestors = walkParents({
          hierarchy: commonStore.$state.groupHierarchy,
          groupId,
        })
        const ancestorIds = Object.keys(ancestors)

        ancestorIds.forEach((ancestorId) => acc.add(ancestorId))
        acc.add(groupId)

        return acc
      }, new Set<GroupId>())

      // intersect again with the entire group list,
      // to return the results + their ancestors in the correct order -
      // the Set deduplication above might put a node in the wrong place.
      return intersection(commonStore.getGroupListPerDisplay, [
        ...groupIdsAndAncestorIds,
      ])
    },
  },
})
